import React, {ReactElement, useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useHtmlIframe} from '../../../../hooks/useHtmlIFrame';
import {useSaveRunfile} from '../../../../hooks/useSaveRunfile';
import {IElementHtml} from '../../../../incase-domain/elements/html/html';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import {isHtmlQuestionReportElement, isHtmlSlottedElement} from '../../../../incase-domain/elements/html/htmlSlot';
import {ElementInteraction, IRunFile} from '../../../../incase-domain/proptypes/runfile';
import {jumpToSlideWithIdAction} from '../../../../store/actions/presentation.actions';
import {loadedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {runFileCustomVarsOnlySelector, runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';
import HtmlQuestionReportBridge from './HtmlQuestionReportBridge';
import HtmlSlottedElementPortal from './HtmlSlottedElementPortal';

interface ElementHtmlProps {
    element: IElementHtml;
    device?: DeviceType;
}

function ElementHtml(props: ElementHtmlProps): ReactElement {
    const {element, device = DeviceType.DESKTOP} = props;
    const {slideChange} = element;
    const runfileWithCustomVars = useSelector(runFileCustomVarsOnlySelector);
    const presentation = useSelector(loadedPresentationSelector);
    const interactionsBySlide = useSelector(runFileElementInteractionsSelector);
    const savePresentationRunFile = useSaveRunfile();
    const dispatch = useDispatch();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [slotRoots, setSlotRoots] = useState<Record<string, HTMLElement>>({});

    const {slottedElements, reportSlideName} = useMemo(() => {
        const slide = presentation?.slides?.find(
            (item) => item._id === element.slideId || item.elements?.some((slideElement) => slideElement.id === element.id)
        );
        return {
            slottedElements: (slide?.elements ?? []).filter(isHtmlSlottedElement),
            reportSlideName: slide?.name ?? '',
        };
    }, [element.id, element.slideId, presentation?.slides]);

    const propagateClick = (): void => {
        wrapperRef.current?.click();
    };

    const handleDocumentReady = useCallback((doc: Document) => {
        const next: Record<string, HTMLElement> = {};
        doc.querySelectorAll<HTMLElement>('[data-incase-slot]').forEach((node) => {
            const slotId = node.getAttribute('data-slot-id');
            if (slotId) next[slotId] = node;
        });
        setSlotRoots(next);
    }, []);

    const {containerRef} = useHtmlIframe(element, runfileWithCustomVars as IRunFile, propagateClick, handleDocumentReady);

    return (
        <div
            ref={wrapperRef}
            style={{width: '100%', height: '100%'}}
            onClick={() => slideChange?.slideId && dispatch(jumpToSlideWithIdAction(slideChange.slideId, true))}
        >
            <div
                style={{
                    background: element.color,
                    color: element.fontColor,
                    width: '100%',
                    height: '100%',
                }}
                className="c-html"
                id={`html-id-${element.id}`}
                ref={containerRef}
            />
            {slottedElements.map((slotted) => {
                const slotId = slotted.htmlSlotId;
                const root = slotId ? slotRoots[slotId] : undefined;
                if (!root) return null;

                if (isHtmlQuestionReportElement(slotted)) {
                    return (
                        <HtmlQuestionReportBridge
                            key={slotted.id}
                            element={slotted}
                            slot={root}
                            slides={presentation?.slides ?? []}
                            slideName={reportSlideName}
                        />
                    );
                }

                const slideInteractions = interactionsBySlide?.[element.slideId];
                const elementInteractions = (slideInteractions?.[slotted.id] ?? {}) as ElementInteraction;

                return createPortal(
                    <HtmlSlottedElementPortal
                        element={slotted}
                        device={device}
                        elementInteractions={elementInteractions}
                        savePresentationRunFile={(elementRunFile, newPoints) => {
                            savePresentationRunFile(elementRunFile, slotted.id, element.slideId, newPoints);
                        }}
                    />,
                    root,
                    slotted.id
                );
            })}
        </div>
    );
}

export default ElementHtml;
